import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-7d46fd29"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "certificate-page" }
const _hoisted_2 = { class: "certificate-page__row-actions" }

import { computed, onMounted, reactive, ref, shallowRef } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Delete, Edit, Plus } from '@element-plus/icons-vue'
import {
  CrudDialog,
  CrudForm,
  CrudSearch,
  CrudTable
} from '@/components/crud-v2'
import { usePagedCrud } from '@/hooks/crud/usePagedCrud'
import { usePermission } from '@/hooks/usePermission'
import {
  certificateFormItems,
  certificateSearchItems,
  createCertificateTableColumns
} from './certificate.config'
import type { CertificateForm } from './certificate.config'
import {
  createCertificate,
  deleteCertificate,
  fetchCertificatePage,
  updateCertificate
} from './certificate.service'
import type {
  CertificatePayload,
  CertificateRecord
} from './certificate.service'

type FormRef = InstanceType<typeof CrudForm>
type DialogMode = 'create' | 'edit'


export default /*#__PURE__*/_defineComponent({
  __name: 'CertificateManage',
  setup(__props) {

const canList = usePermission('certificate', 'list')
const canCreate = usePermission('certificate', 'create')
const canUpdate = usePermission('certificate', 'update')
const canDelete = usePermission('certificate', 'delete')
const certificateTableColumns = createCertificateTableColumns(
  canUpdate || canDelete
)

const {
  filters,
  pageNo,
  pageSize,
  records,
  total,
  error,
  loading,
  load,
  search,
  reset,
  changePage,
  changePageSize,
  refresh,
  refreshAfterDelete
} = usePagedCrud({
  createFilters: () => ({ name: '' }),
  fetchPage: fetchCertificatePage
})

const errorMessage = computed(() =>
  error.value instanceof Error ? error.value.message : ''
)

const dialogVisible = ref(false)
const dialogMode = ref<DialogMode>('create')
const formRef = ref<FormRef>()
const editingRecord = shallowRef<CertificateRecord>()
const form = reactive<CertificateForm>({
  name: '',
  ip: '',
  certificate: '',
  endTime: undefined,
  aliAccount: ''
})

const dialogTitle = computed(() =>
  dialogMode.value === 'create' ? '新增项目' : '编辑项目'
)

const rules = {
  name: { required: true, message: '项目名称是必传的', trigger: 'blur' },
  ip: { required: true, message: '服务器IP是必传的', trigger: 'blur' },
  certificate: {
    required: true,
    message: '证书名称是必传的',
    trigger: 'blur'
  },
  aliAccount: {
    required: true,
    message: '阿里云账号是必传的',
    trigger: 'blur'
  }
}

function resetForm() {
  form.name = ''
  form.ip = ''
  form.certificate = ''
  form.endTime = undefined
  form.aliAccount = ''
}

function openCreateDialog() {
  dialogMode.value = 'create'
  editingRecord.value = undefined
  resetForm()
  dialogVisible.value = true
}

function openEditDialog(row: CertificateRecord) {
  dialogMode.value = 'edit'
  editingRecord.value = row
  form.name = row.name
  form.ip = row.ip
  form.certificate = row.certificate
  form.endTime = row.endTime
  form.aliAccount = row.aliAccount
  dialogVisible.value = true
}

async function submitForm() {
  if (!(await formRef.value!.validate())) return false

  const payload: CertificatePayload = {
    name: form.name,
    ip: form.ip,
    certificate: form.certificate,
    endTime: form.endTime,
    aliAccount: form.aliAccount
  }

  if (dialogMode.value === 'create') {
    await createCertificate(payload)
  } else {
    await updateCertificate(editingRecord.value!.id, payload)
  }
  return true
}

async function handleSubmitSuccess() {
  ElMessage.success(dialogMode.value === 'create' ? '新增成功' : '编辑成功')
  await refresh()
}

function handleSubmitError(cause: unknown) {
  ElMessage.error(cause instanceof Error ? cause.message : '操作失败')
}

const deletingId = ref<CertificateRecord['id']>()

async function handleDelete(row: CertificateRecord) {
  try {
    await ElMessageBox.confirm('确认删除此项目吗？', '提示', {
      confirmButtonText: '确认',
      cancelButtonText: '取消',
      type: 'warning'
    })
  } catch {
    return
  }

  deletingId.value = row.id
  try {
    await deleteCertificate(row.id)
    ElMessage.success('删除成功')
    await refreshAfterDelete()
  } catch (cause) {
    ElMessage.error(cause instanceof Error ? cause.message : '删除失败')
  } finally {
    deletingId.value = undefined
  }
}

onMounted(() => {
  if (canList) load()
})

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(CrudSearch), {
      model: _unref(filters),
      items: _unref(certificateSearchItems),
      loading: _unref(loading),
      onSearch: _unref(search),
      onReset: _unref(reset)
    }, null, 8, ["model", "items", "loading", "onSearch", "onReset"]),
    _createVNode(_unref(CrudTable), {
      rows: _unref(records),
      "page-no": _unref(pageNo),
      "page-size": _unref(pageSize),
      total: _unref(total),
      loading: _unref(loading),
      "error-message": errorMessage.value,
      columns: _unref(certificateTableColumns),
      onPageChange: _unref(changePage),
      onPageSizeChange: _unref(changePageSize),
      onRetry: _unref(refresh)
    }, {
      title: _withCtx(() => [
        _createTextVNode("证书列表")
      ]),
      actions: _withCtx(() => [
        (_unref(canCreate))
          ? (_openBlock(), _createBlock(_component_el_button, {
              key: 0,
              type: "primary",
              onClick: openCreateDialog
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(Plus), {
                  class: "certificate-page__button-icon",
                  "aria-hidden": "true"
                }),
                _createTextVNode(" 新增 ")
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      rowActions: _withCtx(({ row }) => [
        _createElementVNode("div", _hoisted_2, [
          (_unref(canUpdate))
            ? (_openBlock(), _createBlock(_component_el_button, {
                key: 0,
                type: "text",
                onClick: ($event: any) => (openEditDialog(row))
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Edit), {
                    class: "certificate-page__button-icon",
                    "aria-hidden": "true"
                  }),
                  _createTextVNode(" 编辑 ")
                ]),
                _: 2
              }, 1032, ["onClick"]))
            : _createCommentVNode("", true),
          (_unref(canDelete))
            ? (_openBlock(), _createBlock(_component_el_button, {
                key: 1,
                type: "text",
                loading: deletingId.value === row.id,
                disabled: deletingId.value !== undefined,
                onClick: ($event: any) => (handleDelete(row))
              }, {
                default: _withCtx(() => [
                  (deletingId.value !== row.id)
                    ? (_openBlock(), _createBlock(_unref(Delete), {
                        key: 0,
                        class: "certificate-page__button-icon",
                        "aria-hidden": "true"
                      }))
                    : _createCommentVNode("", true),
                  _createTextVNode(" 删除 ")
                ]),
                _: 2
              }, 1032, ["loading", "disabled", "onClick"]))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }, 8, ["rows", "page-no", "page-size", "total", "loading", "error-message", "columns", "onPageChange", "onPageSizeChange", "onRetry"]),
    _createVNode(_unref(CrudDialog), {
      modelValue: dialogVisible.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((dialogVisible).value = $event)),
      title: dialogTitle.value,
      submit: submitForm,
      onSuccess: handleSubmitSuccess,
      onSubmitError: handleSubmitError
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(CrudForm), {
          ref_key: "formRef",
          ref: formRef,
          model: form,
          items: _unref(certificateFormItems),
          rules: rules
        }, null, 8, ["model", "items"])
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ]))
}
}

})